document.addEventListener('DOMContentLoaded', () => {
  // Supported currencies mapped to countries
  const COUNTRY_TO_CURRENCY = {
    US: 'USD',
    CA: 'CAD',
    FR: 'EUR',
    DE: 'EUR',
    IT: 'EUR',
    ES: 'EUR',
    PT: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    AT: 'EUR',
    IE: 'EUR',
    GB: 'GBP',
    AU: 'AUD'
  };

  // Countries that should default to French language
  const FRENCH_SPEAKING_COUNTRIES = ['FR'];

  // Storage keys
  const STORAGE_KEYS = {
    manualLanguage: 'language', // Keep original key for backward compatibility with existing customers
    sessionInitialized: 'localization_session_initialized'
  };

  // Get current Shopify localization state
  function getShopifyState() {
    const state = window.theme?.localization || {};
    return {
      country: state.country || 'US',
      language: (state.language || 'en').toLowerCase().slice(0, 2) // Normalize to 2-char code
    };
  }

  // Get language from URL path (e.g., /fr-ca/, /fr/, /en-ca/)
  function getLanguageFromURL() {
    const path = window.location.pathname.toLowerCase();

    // Check for language prefixes in the URL
    if (path.startsWith('/fr-ca') || path.startsWith('/fr/') || path === '/fr') {
      return 'fr';
    }
    if (path.startsWith('/en-ca') || path.startsWith('/en/') || path === '/en') {
      return 'en';
    }

    return null; // No language prefix found
  }

  // Check if we're on the root path (no language/market prefix)
  function isRootPath() {
    const path = window.location.pathname;
    // Root path is exactly "/" or empty
    return path === '/' || path === '';
  }

  // Get user's manual language selection from localStorage
  function getManualLanguageSelection() {
    return localStorage.getItem(STORAGE_KEYS.manualLanguage);
  }

  // Save user's manual language selection
  function setManualLanguageSelection(language) {
    localStorage.setItem(STORAGE_KEYS.manualLanguage, language);
  }

  // Check if session is already initialized (to avoid multiple detections per session)
  function isSessionInitialized() {
    return sessionStorage.getItem(STORAGE_KEYS.sessionInitialized) === 'true';
  }

  // Mark session as initialized
  function markSessionInitialized() {
    sessionStorage.setItem(STORAGE_KEYS.sessionInitialized, 'true');
  }

  // Detect location using Shopify's browsing_context_suggestions.json
  async function detectLocation() {
    try {
      const response = await fetch('/browsing_context_suggestions.json');
      const data = await response.json();

      let country = 'US';

      // Check for country_name format
      if (data.country_name) {
        const countryMap = {
          'Canada': 'CA',
          'United States': 'US',
          'France': 'FR',
          'United Kingdom': 'GB',
          'Australia': 'AU',
          'Germany': 'DE',
          'Italy': 'IT',
          'Spain': 'ES',
          'Portugal': 'PT',
          'Netherlands': 'NL',
          'Belgium': 'BE',
          'Austria': 'AT',
          'Ireland': 'IE'
        };
        country = countryMap[data.country_name] || 'US';
      }
      // Check for nested detected_values format
      else if (data.detected_values?.country?.handle) {
        country = data.detected_values.country.handle.toUpperCase();
      }
      // Check for country code directly
      else if (data.country) {
        country = data.country.toUpperCase();
      }

      return { country };
    } catch (error) {
      console.error('Error fetching location from Shopify:', error);
      return { country: 'US' };
    }
  }

  // Determine the target country based on detected location
  // Maps to supported countries, falls back to US for unsupported
  function getTargetCountry(detectedCountry) {
    // If the detected country has a mapped currency, use it
    if (COUNTRY_TO_CURRENCY[detectedCountry]) {
      return detectedCountry;
    }
    // Default to US for unsupported countries
    return 'US';
  }

  // Determine the target language based on rules
  function getTargetLanguage(detectedCountry) {
    // Rule 1: URL path takes precedence
    const urlLanguage = getLanguageFromURL();
    if (urlLanguage) {
      return urlLanguage;
    }

    // Rule 2: Manual selection takes precedence (if exists)
    const manualSelection = getManualLanguageSelection();
    if (manualSelection) {
      return manualSelection;
    }

    // Rule 3: Only auto-detect language if on root path
    if (!isRootPath()) {
      // Not on root and no URL language - use current Shopify language
      return getShopifyState().language;
    }

    // Rule 4: Auto-detect based on country (only France gets French)
    if (FRENCH_SPEAKING_COUNTRIES.includes(detectedCountry)) {
      return 'fr';
    }

    return 'en';
  }

  // Submit localization form (causes page refresh)
  function submitLocalizationForm({ country, language }) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    form.style.display = 'none';

    const inputs = `
      <input type="hidden" name="_method" value="PUT">
      ${country ? `<input type="hidden" name="country_code" value="${country}">` : ''}
      ${language ? `<input type="hidden" name="language_code" value="${language}">` : ''}
      <input type="hidden" name="return_to" value="${window.location.pathname}${window.location.search}${window.location.hash}">
    `;

    form.innerHTML = inputs;
    document.body.appendChild(form);
    form.submit();
  }

  // Initialize currency selectors (dropdowns)
  function initializeCurrencySelectors() {
    const shopifyState = getShopifyState();

    document.querySelectorAll('.currency-selector select').forEach(select => {
      select.value = shopifyState.country;

      select.addEventListener('change', () => {
        const selectedCountry = select.value;
        submitLocalizationForm({ country: selectedCountry });
      });
    });
  }

  // Initialize language selectors (buttons)
  function initializeLanguageSelectors() {
    const shopifyState = getShopifyState();

    document.querySelectorAll('.language-selector button').forEach(button => {
      const buttonLang = button.getAttribute('data-language');
      button.classList.toggle('active', buttonLang === shopifyState.language);

      button.addEventListener('click', () => {
        const selectedLanguage = button.getAttribute('data-language');
        // Save manual selection
        setManualLanguageSelection(selectedLanguage);
        submitLocalizationForm({ language: selectedLanguage });
      });
    });
  }

  // Show all localization wrappers (they start hidden to prevent flash)
  function showLocalizationWrappers() {
    document.querySelectorAll('.localization-wrapper').forEach(wrapper => {
      wrapper.classList.add('is-visible');
    });
  }

  // Main initialization
  async function initialize() {
    const shopifyState = getShopifyState();

    // Check if this session has already been initialized
    if (isSessionInitialized()) {
      // Session already processed - just init UI and return
      initializeCurrencySelectors();
      initializeLanguageSelectors();
      showLocalizationWrappers();
      return;
    }

    // Mark session as initialized to prevent re-processing
    markSessionInitialized();

    // Detect user's location
    const { country: detectedCountry } = await detectLocation();

    // Determine target values
    const targetCountry = getTargetCountry(detectedCountry);
    const targetLanguage = getTargetLanguage(detectedCountry);

    // Compare with current Shopify state
    const needsCountryChange = targetCountry !== shopifyState.country;
    const needsLanguageChange = targetLanguage !== shopifyState.language;

    // Only submit form if something needs to change
    if (needsCountryChange || needsLanguageChange) {
      // Build the form data - only include values that need to change
      const formData = {};

      if (needsCountryChange) {
        formData.country = targetCountry;
      }

      if (needsLanguageChange) {
        formData.language = targetLanguage;
      }

      // Submit and refresh
      submitLocalizationForm(formData);
      return; // Page will refresh
    }

    // No changes needed - initialize UI normally
    initializeCurrencySelectors();
    initializeLanguageSelectors();
    showLocalizationWrappers();
  }

  initialize();
});
